import { useEffect, useMemo, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react'
import { FloatingMenu, type FloatingMenuItem } from './FloatingMenu'
import type { Rect } from '@embedpdf/models'

interface ScreenshotOverlayProps {
  pageIndex: number
  rect: Rect
  zoomLevel: number
  onSave: () => void
  onCancel: () => void
}

function pageRectToScreen(pageIndex: number, pdfRect: Rect, zoom: number): DOMRect | null {
  const pageEl = document.getElementById(`embedpdf-page-${pageIndex}`)
  if (!pageEl) return null

  const canvas = pageEl.querySelector('canvas')
  const bounds = canvas ? canvas.getBoundingClientRect() : pageEl.getBoundingClientRect()
  if (bounds.width === 0 || bounds.height === 0) return null

  const sx = bounds.left + pdfRect.origin.x * zoom
  const sy = bounds.top + pdfRect.origin.y * zoom
  const sw = pdfRect.size.width * zoom
  const sh = pdfRect.size.height * zoom

  return {
    left: sx,
    top: sy,
    width: Math.max(sw, 2),
    height: Math.max(sh, 2),
    right: sx + Math.max(sw, 2),
    bottom: sy + Math.max(sh, 2),
    x: sx,
    y: sy,
  } as DOMRect
}

export function ScreenshotOverlay({
  pageIndex,
  rect,
  zoomLevel,
  onSave,
  onCancel,
}: ScreenshotOverlayProps) {
  const { t } = useTranslation()
  const [screenRect, setScreenRect] = useState<DOMRect | null>(null)
  const rafRef = useRef<number>()

  useEffect(() => {
    const update = () => {
      const sr = pageRectToScreen(pageIndex, rect, zoomLevel)
      setScreenRect(sr)
      rafRef.current = requestAnimationFrame(update)
    }
    update()
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [pageIndex, rect, zoomLevel])

  const virtualElement = useMemo(() => {
    if (!screenRect) return null
    const centerX = screenRect.left + screenRect.width / 2
    const top = screenRect.top
    return {
      getBoundingClientRect: () => {
        return {
          x: centerX,
          y: top,
          width: 0,
          height: 0,
          top,
          left: centerX,
          right: centerX,
          bottom: top,
        }
      },
    }
  }, [screenRect])

  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: virtualElement ? autoUpdate : undefined,
  })

  useEffect(() => {
    if (virtualElement) {
      refs.setPositionReference(virtualElement as unknown as HTMLElement)
    }
  }, [virtualElement, refs])

  const menuItems: FloatingMenuItem[] = useMemo(
    () => [
      {
        id: 'save',
        icon: 'save',
        iconColor: '#22C55E',
        tooltip: t('tooltips.saveScreenshot') || 'Save',
        onClick: onSave,
      },
      {
        id: 'cancel',
        icon: 'trash',
        iconColor: '#EF4444',
        tooltip: t('tooltips.cancelScreenshot') || 'Cancel',
        onClick: onCancel,
      },
    ],
    [t, onSave, onCancel],
  )

  const outlineStyle = screenRect
    ? {
        position: 'fixed' as const,
        left: screenRect.left,
        top: screenRect.top,
        width: screenRect.width,
        height: screenRect.height,
        border: '2px dashed #3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        pointerEvents: 'none' as const,
        zIndex: 9998,
      }
    : undefined

  return createPortal(
    <>
      {outlineStyle && <div style={outlineStyle} />}
      {screenRect && (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, zIndex: 9999 }}
        >
          <FloatingMenu items={menuItems} />
        </div>
      )}
    </>,
    document.body,
  )
}
